import React from 'react';
import {connect} from 'react-redux';
import {Container, Row, Col, Button} from 'reactstrap';
import {requestDirectory} from '../actions/directoryActions';
import {requestHistory} from '../actions/historyActions';
import {propertyValueRequest} from '../actions/propertyActions';
import Chart from './Chart';
import Loader from './Loader';
import ProgramsWidget from './dashboard/ProgramsWidget';
import SchedulerWidget from './dashboard/SchedulerWidget';
import ToggleValueIndicator from './ToggleValueIndicator';
import {extrapolateSeries, isHistoryLoaded} from '../services/utils';

const buildTempOptions = (int, ext) => {
    const now = new Date();
    const {samples: intSamples, zones: intZones} = extrapolateSeries(int, now);
    const {samples: extSamples, zones: extZones} = extrapolateSeries(ext, now);
    return {
        chart: {type: 'spline'},
        title: {text: 'Temperature'},
        xAxis: {minTickInterval: 60 * 60 * 24, type: 'datetime'},
        yAxis: {minTickInterval: 1, title: {enabled: false}},
        series: [
            {data: intSamples, name: 'Int', zones: intZones, zoneAxis: 'x'},
            {data: extSamples, name: 'Ext', zones: extZones, zoneAxis: 'x'},
        ],
    };
};

const buildHumOptions = (int, ext) => {
    const now = new Date();
    const {samples: intSamples, zones: intZones} = extrapolateSeries(int, now);
    const {samples: extSamples, zones: extZones} = extrapolateSeries(ext, now);
    return {
        chart: {type: 'spline'},
        title: {text: 'Humidity'},
        xAxis: {minTickInterval: 60 * 60 * 24, type: 'datetime'},
        yAxis: {minTickInterval: 10, title: {enabled: false}, min: 0, max: 100},
        series: [
            {data: intSamples, name: 'Int', zones: intZones, zoneAxis: 'x'},
            {data: extSamples, name: 'Ext', zones: extZones, zoneAxis: 'x'},
        ],
    };
};

const buildPressureOptions = (data) => {
    const now = new Date();
    const {samples, zones} = extrapolateSeries(data, now);
    return {
        chart: {type: 'spline'},
        title: {text: 'Pressure'},
        xAxis: {minTickInterval: 60 * 60 * 24, type: 'datetime'},
        yAxis: {minTickInterval: 1, title: {enabled: false}},
        series: [
            {data: samples, name: 'Int', zones, zoneAxis: 'x'},
        ],
    };
};

const buildZoneSeries = (zone, index, date) => {
    const samples = [];
    let lastOffset;
    if (zone.samples.length > 0) {
        samples.push([new Date(zone.samples[0].date).getTime(), zone.samples[0].value ? 1 : 0]);
        let i = 1;
        while (i < zone.samples.length) {
            samples.push([new Date(zone.samples[i].date).getTime() - 1, samples[samples.length - 1][1]]);
            samples.push([new Date(zone.samples[i].date).getTime(), zone.samples[i].value ? 1 : 0]);
            i++;
        }
        lastOffset = samples[samples.length - 1][0];
        samples.push([date.getTime(), samples[samples.length - 1][1]]);
    }
    return {
        data: samples,
        name: `Zone${index + 1}`,
        zones: lastOffset > 0 ? [{value: lastOffset}, {dashStyle: 'ShortDot'}] : undefined,
        zoneAxis: 'x',
    };
};

const buildZonesOptions = (...zones) => {
    const now = new Date();
    return {
        chart: {type: 'area'},
        title: {text: 'Zones'},
        xAxis: {minTickInterval: 60 * 60 * 24, type: 'datetime'},
        yAxis: {minTickInterval: 1, title: {enabled: false}, min: 0, max: 1},
        series: zones.map((zone, index) => buildZoneSeries(zone, index, now)),
    };
};

class Dashboard extends React.Component {
    constructor(props) {
        super(props);
        this.handleStartProgram = this.handleStartProgram.bind(this);
        this.handleStopProgram = this.handleStopProgram.bind(this);
        this.handleUpdateScheduler = this.handleUpdateScheduler.bind(this);
        this.handlePropertyValueRequest = this.handlePropertyValueRequest.bind(this);
        this.load = this.load.bind(this);
    }

    componentDidMount() {
        this.props.requestDirectory();
        this.load();
    }

    load() {
        const from = new Date();
        from.setHours(from.getHours() - 24);
        const to = new Date();
        this.props.requestHistory('extTemp', 'Arduino', 'ExtTemp', {from, to});
        this.props.requestHistory('intTemp', 'IntArduino', 'Temp', {from, to});
        this.props.requestHistory('extHum', 'Arduino', 'Humidity', {from, to});
        this.props.requestHistory('intHum', 'IntArduino', 'Humidity', {from, to});
        this.props.requestHistory('pressure', 'IntArduino', 'Pressure', {from, to});
        this.props.requestHistory('zone1', 'Arduino', 'Zone1', {from, to});
        this.props.requestHistory('zone2', 'Arduino', 'Zone2', {from, to});
        this.props.requestHistory('zone3', 'Arduino', 'Zone3', {from, to});
        this.props.requestHistory('zone4', 'Arduino', 'Zone4', {from, to});
        this.props.requestHistory('zone5', 'Arduino', 'Zone5', {from, to});
    }

    handleStartProgram(node, property) {
        this.props.propertyValueRequest(node.id, property.id, true);
    }

    handleStopProgram(node, property) {
        this.props.propertyValueRequest(node.id, property.id, false);
    }

    handleUpdateScheduler(node, property, value) {
        this.props.propertyValueRequest(node.id, property.id, value);
    }

    handlePropertyValueRequest(path, value) {
        if (confirm(`Set "${path}" to "${value}"?`)) {
            const [nodeId, propertyId] = path.split('/', 2);
            this.props.propertyValueRequest(nodeId, propertyId, value);
        }
    }

    render() {
        const {directory, intTemp, extTemp, intHum, extHum, pressure, zone1, zone2, zone3, zone4, zone5, values} = this.props;
        return (
            <Container fluid>
                <Button onClick={this.load}>Reload</Button>
                <Row>
                    <Col xs={6} sm={4} md={3}>
                        <div className="dashboard-item">
                            Ext: <strong>{values['Arduino/ExtTemp']}</strong>°C<br />
                            Int: <strong>{values['IntArduino/Temp']}</strong>°C
                        </div>
                    </Col>
                    <Col xs={6} sm={4} md={3}>
                        <div className="dashboard-item">
                            Ext: <strong>{values['Arduino/Humidity']}</strong>%<br />
                            Int: <strong>{values['IntArduino/Humidity']}</strong>%
                        </div>
                    </Col>
                    <Col xs={6} sm={4} md={3}>
                        <div className="dashboard-item">
                            Pressure: <strong>{values['IntArduino/Pressure']}</strong>mb
                        </div>
                    </Col>
                    <Col xs={6} sm={4} md={3}>
                        <div className="dashboard-item">
                            Zones:
                            <ToggleValueIndicator path="Arduino/Zone1" value={values['Arduino/Zone1']} onClick={this.handlePropertyValueRequest} />
                            <ToggleValueIndicator path="Arduino/Zone2" value={values['Arduino/Zone2']} onClick={this.handlePropertyValueRequest} />
                            <ToggleValueIndicator path="Arduino/Zone3" value={values['Arduino/Zone3']} onClick={this.handlePropertyValueRequest} />
                            <ToggleValueIndicator path="Arduino/Zone4" value={values['Arduino/Zone4']} onClick={this.handlePropertyValueRequest} />
                            <ToggleValueIndicator path="Arduino/Zone5" value={values['Arduino/Zone5']} onClick={this.handlePropertyValueRequest} />
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col sm={6} md={4}>
                        <div className="dashboard-item">
                            {isHistoryLoaded(intTemp, extTemp) && <Chart options={buildTempOptions(intTemp.data, extTemp.data)} /> || <Loader />}
                        </div>
                    </Col>
                    <Col sm={6} md={4}>
                        <div className="dashboard-item">
                            {isHistoryLoaded(intHum, extHum) && <Chart options={buildHumOptions(intHum.data, extHum.data)} /> || <Loader />}
                        </div>
                    </Col>
                    <Col sm={6} md={4}>
                        <div className="dashboard-item">
                            {isHistoryLoaded(pressure) && <Chart options={buildPressureOptions(pressure.data)} /> || <Loader />}
                        </div>
                    </Col>
                    <Col sm={6}>
                        <div className="dashboard-item">
                            {isHistoryLoaded(zone1, zone2, zone3, zone4, zone5) &&
                                <Chart options={buildZonesOptions(zone1.data, zone2.data, zone3.data, zone4.data, zone5.data)} /> || <Loader />}
                        </div>
                    </Col>
                    <Col sm={3}>
                        <div className="dashboard-item">
                            Ext DewPoint: <strong>{values['ExtCalc/DewPoint']}</strong>°C<br />
                            Int DewPoint: <strong>{values['IntCalc/DewPoint']}</strong>°C<br />
                            Pressure trend: <strong>{values['IntCalc/PressureTrend']}</strong><br />
                            Weather forecast: <strong>{values['IntCalc/ZambrettiLabel']}</strong> <small>({values['IntCalc/Zambretti']})</small><br />
                        </div>
                    </Col>
                    <Col sm={3}>
                        <div className="dashboard-item">
                            {directory &&
                                <ProgramsWidget node={directory.nodes.find(({id}) => id === 'Programs')} values={values}
                                    onStart={this.handleStartProgram} onStop={this.handleStopProgram} /> ||
                                <Loader />}
                        </div>
                        <div className="dashboard-item">
                            {directory &&
                                <SchedulerWidget node={directory.nodes.find(({id}) => id === 'Scheduler')} values={values}
                                    onUpdate={this.handleUpdateScheduler} /> ||
                                <Loader />}
                        </div>
                    </Col>
                </Row>
            </Container>
        );
    }
}

const mapStateToProps = (state) => ({
    directory: state.directory.data,
    extTemp: state.history.data.extTemp,
    intTemp: state.history.data.intTemp,
    extHum: state.history.data.extHum,
    intHum: state.history.data.intHum,
    pressure: state.history.data.pressure,
    zone1: state.history.data.zone1,
    zone2: state.history.data.zone2,
    zone3: state.history.data.zone3,
    zone4: state.history.data.zone4,
    zone5: state.history.data.zone5,
    values: state.property.values,
});

export default connect(mapStateToProps, {propertyValueRequest, requestDirectory, requestHistory})(Dashboard);
